import type { AIModel } from '@/types';
import { buildPhaseMessages, parseCodaOutput, parseRefinerOutput } from './prompts';
import { emitMapleMythosTaskUpdate } from './taskBus';
import { getAICreditSnapshot, getMapleMythosExecutionMode, runMeteredMapleMythosCall } from './creditMetering';
import type {
  MapleMythosPhase,
  MapleMythosRunConfig,
  MapleMythosRunOutput,
  MapleMythosRunState,
  MapleMythosRuntimeDependencies,
  MapleMythosTerminalResult,
  MapleMythosTranscriptEntry,
  MapleMythosWorkingState,
} from './types';

const STORAGE_KEY = 'maplemythos:runs:v1';
const MAX_STORED_RUNS = 12;
const FINAL_STATES = new Set(['completed', 'failed', 'stopped']);
const MAX_AUTO_TERMINAL_STEPS = 8;
const INTERRUPTED_RUN_MESSAGE = 'Run interrupted before it could finish.';
const INTERRUPTED_RUN_ERROR = 'This run was interrupted because the MapleOS browser session was reloaded or closed.';

function createId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `maplemythos-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function hasWindow() {
  return typeof window !== 'undefined';
}

function extractLatestTerminalCommandBlock(content: string) {
  const terminalFence = /```(?:terminal|cmd|mapleos)\s*\n([\s\S]*?)```/gi;
  const matches = [...content.matchAll(terminalFence)];
  const latestMatch = matches.at(-1);

  if (!latestMatch?.[1]?.trim()) {
    return null;
  }

  const raw = latestMatch[1].trim();
  const commands = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^\/cmd\s+/i, ''));

  if (commands.length === 0) {
    return {
      commands: [],
      raw,
      error: 'DeMythos could not parse any MapleOS terminal commands from the fenced block.',
    };
  }

  if (commands.length > MAX_AUTO_TERMINAL_STEPS) {
    return {
      commands: [],
      raw,
      error: `DeMythos only auto-executes up to ${MAX_AUTO_TERMINAL_STEPS} terminal commands per fenced block.`,
    };
  }

  return {
    commands,
    raw,
    error: null,
  };
}

function createWorkingState(): MapleMythosWorkingState {
  return {
    plannerNotes: [],
    criticNotes: [],
    refinedAnswers: [],
    reflections: [],
    latestPlan: '',
    latestCritique: '',
    latestAnswer: '',
    latestSummary: '',
    converged: false,
    confidence: 0,
  };
}

function createTranscriptEntry(entry: Omit<MapleMythosTranscriptEntry, 'id' | 'timestamp'>): MapleMythosTranscriptEntry {
  return {
    id: createId(),
    timestamp: new Date().toISOString(),
    ...entry,
  };
}

function clampLoops(value: number) {
  return Math.max(1, Math.min(50, Math.round(value)));
}

function serializeRun(run: MapleMythosRunState) {
  return run;
}

function hydrateRuns(): MapleMythosRunState[] {
  if (!hasWindow()) return [];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((candidate) => {
      const run = candidate as MapleMythosRunState;
      const normalizedRun: MapleMythosRunState = {
        ...run,
        knowledgeBaseAutoSave: Boolean(run.knowledgeBaseAutoSave),
        knowledgeBaseSaveStatus: run.knowledgeBaseSaveStatus || (run.knowledgeBaseAutoSave ? 'pending' : 'idle'),
      };

      if (!FINAL_STATES.has(normalizedRun.status)) {
        return {
          ...normalizedRun,
          status: 'failed',
          error: INTERRUPTED_RUN_ERROR,
          updatedAt: new Date().toISOString(),
          transcript: [
            ...(normalizedRun.transcript || []),
            createTranscriptEntry({
              kind: 'error',
              message: INTERRUPTED_RUN_MESSAGE,
            }),
          ],
        } satisfies MapleMythosRunState;
      }

      return normalizedRun;
    });
  } catch {
    return [];
  }
}

class MapleMythosRuntime {
  private runs = new Map<string, MapleMythosRunState>();
  private deps = new Map<string, MapleMythosRuntimeDependencies>();
  private listeners = new Set<(runs: MapleMythosRunState[]) => void>();
  private activeExecutions = new Set<string>();

  constructor() {
    hydrateRuns().forEach((run) => {
      this.runs.set(run.id, run);
    });
    this.persist();
  }

  subscribe(listener: (runs: MapleMythosRunState[]) => void) {
    this.listeners.add(listener);
    listener(this.listRuns());

    return () => {
      this.listeners.delete(listener);
    };
  }

  listRuns() {
    return Array.from(this.runs.values()).sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  }

  getRun(runId: string) {
    return this.runs.get(runId);
  }

  attachRunDependencies(runId: string, dependencies: MapleMythosRuntimeDependencies) {
    const run = this.requireRun(runId);
    const existing = this.deps.get(runId);
    const nextDependencies: MapleMythosRuntimeDependencies = {
      ...(existing || {}),
      ...dependencies,
      sshGrant: dependencies.sshGrant ?? existing?.sshGrant ?? run.sshGrant,
    };

    this.deps.set(runId, nextDependencies);

    if (nextDependencies.sshGrant && nextDependencies.sshGrant !== run.sshGrant) {
      this.patchRun(runId, { sshGrant: nextDependencies.sshGrant });
    }

    return this.requireRun(runId);
  }

  async startRun(config: MapleMythosRunConfig, dependencies: MapleMythosRuntimeDependencies) {
    const model = this.resolveModel(config.modelId, dependencies.appModels);
    if (!model) {
      throw new Error(`DeMythos could not find model "${config.modelId}".`);
    }

    const now = new Date().toISOString();
    const run: MapleMythosRunState = {
      id: createId(),
      prompt: config.prompt.trim(),
      modelId: model.id,
      modelName: model.name,
      modelProvider: model.provider,
      executionMode: getMapleMythosExecutionMode(model),
      status: 'running',
      maxLoops: clampLoops(config.maxLoops),
      currentLoop: 0,
      reasoningMode: config.reasoningMode,
      returnMode: config.returnMode,
      stopOnConvergence: config.stopOnConvergence,
      tokenBudget: config.budget.tokenBudget,
      creditBudget: config.budget.creditBudget,
      timeBudgetMs: config.budget.timeBudgetMs,
      tokensUsed: 0,
      creditsUsed: 0,
      startedAt: now,
      updatedAt: now,
      transcript: [
        createTranscriptEntry({
          kind: 'status',
          phase: 'prelude',
          message: 'DeMythos run initialized.',
          content: `Prompt length: ${config.prompt.trim().length} characters`,
        }),
      ],
      workingState: createWorkingState(),
      finalAnswer: '',
      finalSummary: '',
      origin: config.origin || 'app',
      title: config.title || 'DeMythos Run',
      sshGrant: dependencies.sshGrant,
      knowledgeBaseAutoSave: Boolean(config.knowledgeBaseAutoSave),
      knowledgeBaseSaveStatus: config.knowledgeBaseAutoSave ? 'pending' : 'idle',
    };

    if (dependencies.sshGrant) {
      run.transcript.push(
        createTranscriptEntry({
          kind: 'control',
          phase: 'prelude',
          message: `SSH access granted through profile ${dependencies.sshGrant.name}.`,
          content: `${dependencies.sshGrant.username}@${dependencies.sshGrant.host}:${dependencies.sshGrant.port}`,
        })
      );
    }

    this.deps.set(run.id, dependencies);
    this.runs.set(run.id, run);
    this.publish(run.id);
    void this.executeRun(run.id);

    return this.requireRun(run.id);
  }

  pauseRun(runId: string) {
    const run = this.requireRun(runId);
    if (run.status !== 'running') return run;

    this.patchRun(runId, {
      status: 'paused',
      transcript: [
        ...run.transcript,
        createTranscriptEntry({
          kind: 'control',
          message: 'Run paused. DeMythos will stop after the current phase checkpoint.',
        }),
      ],
    });

    return this.requireRun(runId);
  }

  resumeRun(runId: string) {
    const run = this.requireRun(runId);
    const resumableInterruption = this.isResumableInterruptedRun(run);
    if (run.status !== 'paused' && !resumableInterruption) return run;

    this.patchRun(runId, {
      status: 'running',
      error: undefined,
      transcript: [
        ...run.transcript,
        createTranscriptEntry({
          kind: 'control',
          message: resumableInterruption ? 'Run resumed after interruption.' : 'Run resumed.',
        }),
      ],
    });

    void this.executeRun(runId);
    return this.requireRun(runId);
  }

  stopRun(runId: string) {
    const run = this.requireRun(runId);
    if (FINAL_STATES.has(run.status)) return run;

    this.patchRun(runId, {
      status: 'stopped',
      finalAnswer: run.finalAnswer || run.workingState.latestAnswer,
      finalSummary: run.finalSummary || run.workingState.latestSummary,
      transcript: [
        ...run.transcript,
        createTranscriptEntry({
          kind: 'control',
          message: 'Run stopped by user.',
        }),
      ],
    });

    return this.requireRun(runId);
  }

  deleteRun(runId: string) {
    const run = this.requireRun(runId);
    if (run.status === 'running' || run.status === 'paused' || this.activeExecutions.has(runId)) {
      throw new Error('Stop the active DeMythos run before deleting it.');
    }

    this.deps.delete(runId);
    this.runs.delete(runId);
    this.broadcast();

    return this.listRuns();
  }

  updateRunKnowledgeBase(
    runId: string,
    updates: Partial<Pick<
      MapleMythosRunState,
      'knowledgeBaseSaveStatus' | 'knowledgeBaseId' | 'knowledgeBaseName' | 'knowledgeBaseError' | 'knowledgeBaseSavedAt'
    >>,
    transcriptMessage?: string,
  ) {
    const run = this.requireRun(runId);
    this.patchRun(runId, {
      ...updates,
      transcript: transcriptMessage
        ? [
            ...run.transcript,
            createTranscriptEntry({
              kind: 'control',
              message: transcriptMessage,
            }),
          ]
        : run.transcript,
    });

    return this.requireRun(runId);
  }

  updateRunConfig(
    runId: string,
    updates: Partial<Pick<MapleMythosRunConfig, 'maxLoops' | 'reasoningMode' | 'stopOnConvergence'>> & {
      budget?: Partial<MapleMythosRunConfig['budget']>;
    }
  ) {
    const run = this.requireRun(runId);
    const next: MapleMythosRunState = {
      ...run,
      maxLoops: updates.maxLoops ? clampLoops(updates.maxLoops) : run.maxLoops,
      reasoningMode: updates.reasoningMode || run.reasoningMode,
      stopOnConvergence: updates.stopOnConvergence ?? run.stopOnConvergence,
      tokenBudget: updates.budget?.tokenBudget ?? run.tokenBudget,
      creditBudget: updates.budget?.creditBudget ?? run.creditBudget,
      timeBudgetMs: updates.budget?.timeBudgetMs ?? run.timeBudgetMs,
      updatedAt: new Date().toISOString(),
      transcript: [
        ...run.transcript,
        createTranscriptEntry({
          kind: 'control',
          message: 'Run configuration updated.',
          meta: {
            maxLoops: updates.maxLoops ?? run.maxLoops,
            reasoningMode: updates.reasoningMode || run.reasoningMode,
          },
        }),
      ],
    };

    this.runs.set(runId, next);
    this.publish(runId);
    return next;
  }

  async waitForCompletion(runId: string) {
    const immediate = this.requireRun(runId);
    if (FINAL_STATES.has(immediate.status)) {
      return this.toOutput(immediate);
    }

    return new Promise<MapleMythosRunOutput>((resolve) => {
      const unsubscribe = this.subscribe((runs) => {
        const run = runs.find((candidate) => candidate.id === runId);
        if (!run || !FINAL_STATES.has(run.status)) {
          return;
        }

        unsubscribe();
        resolve(this.toOutput(run));
      });
    });
  }

  exportRun(runId: string) {
    return JSON.stringify(this.toOutput(this.requireRun(runId)), null, 2);
  }

  private async executeRun(runId: string) {
    if (this.activeExecutions.has(runId)) return;

    this.activeExecutions.add(runId);

    try {
      const initialRun = this.requireRun(runId);
      const model = this.resolveModel(initialRun.modelId, this.requireDeps(runId).appModels);
      if (!model) {
        throw new Error(`DeMythos could not resolve the selected model "${initialRun.modelId}".`);
      }

      if (initialRun.executionMode === 'cloud') {
        const snapshot = await getAICreditSnapshot();
        if (snapshot && snapshot.availableCredits <= 0 && !snapshot.canAutoTopup) {
          this.finishRun(runId, 'failed', {
            error: 'DeMythos stopped before starting because no paid AI credits were available.',
          });
          return;
        }
      }

      while (true) {
        let run = this.requireRun(runId);

        if (run.status === 'paused') {
          return;
        }

        if (FINAL_STATES.has(run.status)) {
          return;
        }

        const stopReason = this.getStopReason(run, false);
        if (stopReason) {
          this.finishRun(runId, run.status === 'stopped' ? 'stopped' : 'completed', {
            finalAnswer: run.finalAnswer || run.workingState.latestAnswer,
            finalSummary: run.finalSummary || run.workingState.latestSummary,
            transcriptEntry: createTranscriptEntry({
              kind: 'budget',
              message: stopReason,
            }),
          });
          return;
        }

        const loopIndex = run.currentLoop + 1;
        this.patchRun(runId, {
          currentLoop: loopIndex,
          transcript: [
            ...run.transcript,
            createTranscriptEntry({
              kind: 'status',
              loopIndex,
              message: `Loop ${loopIndex}/${run.maxLoops} started.`,
            }),
          ],
        });

        await this.runPhase(runId, 'planner', loopIndex, model);
        if (this.shouldYield(runId)) return;

        await this.runPhase(runId, 'critic', loopIndex, model);
        if (this.shouldYield(runId)) return;

        await this.runPhase(runId, 'refiner', loopIndex, model);
        if (this.shouldYield(runId)) return;

        run = this.requireRun(runId);
        const postLoopStop = this.getStopReason(run, true);
        if (postLoopStop) {
          await this.runPhase(runId, 'coda', run.currentLoop, model);
          const finalRun = this.requireRun(runId);
          this.finishRun(runId, finalRun.status === 'stopped' ? 'stopped' : 'completed', {
            finalAnswer: finalRun.finalAnswer || finalRun.workingState.latestAnswer,
            finalSummary: finalRun.finalSummary || finalRun.workingState.latestSummary,
            transcriptEntry: createTranscriptEntry({
              kind: 'budget',
              loopIndex: finalRun.currentLoop,
              message: postLoopStop,
            }),
          });
          return;
        }

        await new Promise((resolve) => window.setTimeout(resolve, 0));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown DeMythos runtime error.';
      this.finishRun(runId, 'failed', { error: message });
    } finally {
      this.activeExecutions.delete(runId);
    }
  }

  private async runPhase(runId: string, phase: MapleMythosPhase, loopIndex: number, model: AIModel) {
    const run = this.requireRun(runId);
    const dependencies = this.requireDeps(runId);

    this.patchRun(runId, {
      transcript: [
        ...run.transcript,
        createTranscriptEntry({
          kind: 'phase',
          phase,
          loopIndex,
          message: `${phase} phase started.`,
        }),
      ],
    });

    const result = await runMeteredMapleMythosCall({
      messages: buildPhaseMessages(phase, this.requireRun(runId)),
      model,
      appModels: dependencies.appModels,
      config: {
        max_tokens: phase === 'critic' ? 900 : phase === 'coda' ? 1200 : 1100,
        usageContext: {
          source: 'demythos',
          feature: 'demythos',
          runId,
          runTitle: run.title,
          phase,
        },
      },
    });

    const latestRun = this.requireRun(runId);
    const nextTranscript = [
      ...latestRun.transcript,
      createTranscriptEntry({
        kind: phase === 'coda' ? 'result' : 'phase',
        phase,
        loopIndex,
        message: `${phase} phase completed.`,
        content: result.content,
        tokensUsed: result.tokensUsed,
        creditsUsed: result.creditsUsed,
      }),
    ];

    const nextWorkingState = { ...latestRun.workingState };
    let finalAnswer = latestRun.finalAnswer;
    let finalSummary = latestRun.finalSummary;

    if (phase === 'planner') {
      nextWorkingState.latestPlan = result.content;
      nextWorkingState.plannerNotes = [...latestRun.workingState.plannerNotes, result.content];
    }

    if (phase === 'critic') {
      nextWorkingState.latestCritique = result.content;
      nextWorkingState.criticNotes = [...latestRun.workingState.criticNotes, result.content];
    }

    if (phase === 'refiner') {
      const parsed = parseRefinerOutput(result.content);
      nextWorkingState.latestAnswer = parsed.answer;
      nextWorkingState.latestSummary = parsed.summary;
      nextWorkingState.converged = parsed.converged;
      nextWorkingState.confidence = parsed.confidence;
      nextWorkingState.refinedAnswers = [...latestRun.workingState.refinedAnswers, parsed.answer];
      nextWorkingState.reflections = [
        ...latestRun.workingState.reflections,
        {
          loopIndex,
          planner: nextWorkingState.latestPlan,
          critic: nextWorkingState.latestCritique,
          refiner: parsed.answer,
          converged: parsed.converged,
          confidence: parsed.confidence,
        },
      ];
    }

    if (phase === 'coda') {
      const parsed = parseCodaOutput(result.content);
      finalAnswer = parsed.finalAnswer;
      finalSummary = parsed.summary;
    }

    this.patchRun(runId, {
      tokensUsed: latestRun.tokensUsed + result.tokensUsed,
      creditsUsed: latestRun.creditsUsed + result.creditsUsed,
      transcript: nextTranscript,
      workingState: nextWorkingState,
      finalAnswer,
      finalSummary,
    });

    if (result.creditSnapshot && result.creditSnapshot.availableCredits <= 0 && !result.creditSnapshot.canAutoTopup) {
      this.patchRun(runId, {
        status: 'stopped',
        transcript: [
          ...this.requireRun(runId).transcript,
          createTranscriptEntry({
            kind: 'budget',
            loopIndex,
            message: 'DeMythos detected that available paid AI credits were exhausted.',
          }),
        ],
      });
    }

    await this.maybeExecuteTerminalCommands(runId, phase, loopIndex, result.content);
  }

  private async maybeExecuteTerminalCommands(runId: string, phase: MapleMythosPhase, loopIndex: number, content: string) {
    const dependencies = this.requireDeps(runId);
    const terminalBlock = extractLatestTerminalCommandBlock(content);

    if (!terminalBlock) {
      return;
    }

    if (!dependencies.terminalBridge) {
      this.patchRun(runId, {
        transcript: [
          ...this.requireRun(runId).transcript,
          createTranscriptEntry({
            kind: 'tool',
            phase,
            loopIndex,
            message: 'DeMythos detected a fenced terminal block, but the MapleOS terminal bridge is unavailable in this session.',
          }),
        ],
      });
      return;
    }

    if (!dependencies.sshGrant) {
      this.patchRun(runId, {
        transcript: [
          ...this.requireRun(runId).transcript,
          createTranscriptEntry({
            kind: 'tool',
            phase,
            loopIndex,
            message: 'DeMythos detected a fenced terminal block, but no SSH profile or terminal tool grant was provided for this run.',
          }),
        ],
      });
      return;
    }

    if (terminalBlock.error || !terminalBlock.commands.length) {
      this.patchRun(runId, {
        transcript: [
          ...this.requireRun(runId).transcript,
          createTranscriptEntry({
            kind: 'tool',
            phase,
            loopIndex,
            message: terminalBlock.error || 'DeMythos could not parse the terminal block.',
            content: terminalBlock.raw,
          }),
        ],
      });
      return;
    }

    for (const command of terminalBlock.commands) {
      const commandResult = await this.executeTerminalCommand(runId, command, dependencies);
      const latestRun = this.requireRun(runId);
      this.patchRun(runId, {
        transcript: [
          ...latestRun.transcript,
          createTranscriptEntry({
            kind: 'tool',
            phase,
            loopIndex,
            message: `$ ${command}`,
            content: commandResult,
          }),
        ],
      });

      if (this.shouldYield(runId)) {
        return;
      }
    }
  }

  private async executeTerminalCommand(runId: string, command: string, dependencies: MapleMythosRuntimeDependencies) {
    if (!dependencies.terminalBridge) {
      return 'Terminal bridge unavailable.';
    }

    let result: MapleMythosTerminalResult = await dependencies.terminalBridge.runCommand(command);
    const lines = [`$ ${command}`];

    while (result.kind === 'input') {
      if (!dependencies.requestInput) {
        lines.push(`Interactive input required: ${result.prompt}`);
        break;
      }

      this.patchRun(runId, {
        status: 'paused',
        transcript: [
          ...this.requireRun(runId).transcript,
          createTranscriptEntry({
            kind: 'control',
            message: `Waiting for operator input: ${result.prompt}`,
          }),
        ],
      });

      const answer = await dependencies.requestInput(result.prompt, { secret: result.secret });
      this.patchRun(runId, {
        status: 'running',
        transcript: [
          ...this.requireRun(runId).transcript,
          createTranscriptEntry({
            kind: 'control',
            message: `Operator input received for prompt: ${result.prompt}`,
          }),
        ],
      });

      result = await dependencies.terminalBridge.submitInput(answer);
    }

    if (result.kind === 'output') {
      lines.push(result.text || 'Command executed.');
    }

    return lines.join('\n\n').trim();
  }

  private getStopReason(run: MapleMythosRunState, allowConvergence: boolean) {
    if (run.status === 'stopped') {
      return 'Run was stopped by the user.';
    }

    if (run.timeBudgetMs && Date.now() - new Date(run.startedAt).getTime() >= run.timeBudgetMs) {
      return 'Wall-clock time budget reached.';
    }

    if (run.tokenBudget && run.tokensUsed >= run.tokenBudget) {
      return 'Token budget reached.';
    }

    if (run.creditBudget && run.creditsUsed >= run.creditBudget) {
      return 'Credit budget reached.';
    }

    if (run.currentLoop >= run.maxLoops) {
      return 'Maximum loop budget reached.';
    }

    if (allowConvergence && run.stopOnConvergence && run.workingState.converged) {
      return 'Convergence heuristic triggered.';
    }

    return null;
  }

  private shouldYield(runId: string) {
    const run = this.requireRun(runId);
    return run.status === 'paused' || FINAL_STATES.has(run.status);
  }

  private finishRun(
    runId: string,
    status: MapleMythosRunState['status'],
    options: {
      finalAnswer?: string;
      finalSummary?: string;
      error?: string;
      transcriptEntry?: MapleMythosTranscriptEntry;
    } = {}
  ) {
    const run = this.requireRun(runId);
    this.patchRun(runId, {
      status,
      finalAnswer: options.finalAnswer ?? run.finalAnswer,
      finalSummary: options.finalSummary ?? run.finalSummary,
      error: options.error,
      transcript: options.transcriptEntry ? [...run.transcript, options.transcriptEntry] : run.transcript,
    });
  }

  private resolveModel(modelId: string, models: AIModel[]) {
    return models.find((candidate) => candidate.id === modelId);
  }

  private isResumableInterruptedRun(run: MapleMythosRunState) {
    if (run.status !== 'failed') {
      return false;
    }

    if (run.error === INTERRUPTED_RUN_ERROR) {
      return true;
    }

    return run.transcript.some((entry) => entry.kind === 'error' && entry.message === INTERRUPTED_RUN_MESSAGE);
  }

  private requireRun(runId: string) {
    const run = this.runs.get(runId);
    if (!run) {
      throw new Error(`DeMythos run "${runId}" was not found.`);
    }

    return run;
  }

  private requireDeps(runId: string) {
    const dependencies = this.deps.get(runId);
    if (!dependencies) {
      throw new Error(`DeMythos runtime dependencies for run "${runId}" were not found.`);
    }

    return dependencies;
  }

  private patchRun(runId: string, updates: Partial<MapleMythosRunState>) {
    const run = this.requireRun(runId);
    const next: MapleMythosRunState = {
      ...run,
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    this.runs.set(runId, next);
    this.publish(runId);
  }

  private publish(runId: string) {
    const run = this.requireRun(runId);
    emitMapleMythosTaskUpdate(run);
    this.broadcast();
  }

  private broadcast() {
    this.persist();
    const snapshot = this.listRuns();
    this.listeners.forEach((listener) => listener(snapshot));
  }

  private persist() {
    if (!hasWindow()) return;

    const serializableRuns = this.listRuns()
      .slice(0, MAX_STORED_RUNS)
      .map(serializeRun);

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(serializableRuns));
  }

  private toOutput(run: MapleMythosRunState): MapleMythosRunOutput {
    return {
      id: run.id,
      finalAnswer: run.finalAnswer || run.workingState.latestAnswer,
      summary: run.finalSummary || run.workingState.latestSummary,
      transcript: run.transcript,
      loopCount: run.currentLoop,
      status: run.status,
      creditsUsed: run.creditsUsed,
      tokensUsed: run.tokensUsed,
      reasoningState: run.workingState,
      executionMode: run.executionMode,
      modelId: run.modelId,
      modelName: run.modelName,
      sshGrant: run.sshGrant,
      knowledgeBaseAutoSave: run.knowledgeBaseAutoSave,
      knowledgeBaseSaveStatus: run.knowledgeBaseSaveStatus,
      knowledgeBaseId: run.knowledgeBaseId,
      knowledgeBaseName: run.knowledgeBaseName,
      knowledgeBaseError: run.knowledgeBaseError,
      knowledgeBaseSavedAt: run.knowledgeBaseSavedAt,
    };
  }
}

export const mapleMythosRuntime = new MapleMythosRuntime();